import { useEffect, useState, useCallback } from 'react';
import { warehouseAPI } from '../lib/api';

const DEFAULT_THEME = {
  buttonColor: null,
  buttonTextColor: null,
  sidebarTop: null,
  sidebarMid: null,
  sidebarBottom: null,
  sidebarText: null,
};

export function applyThemeToDOM(theme) {
  const root = document.documentElement;
  if (!theme) return;

  // Sidebar gradient colors
  if (theme.sidebarTop) root.style.setProperty('--sidebar-top', theme.sidebarTop);
  else root.style.removeProperty('--sidebar-top');

  if (theme.sidebarMid) root.style.setProperty('--sidebar-mid', theme.sidebarMid);
  else root.style.removeProperty('--sidebar-mid');

  if (theme.sidebarBottom) root.style.setProperty('--sidebar-bottom', theme.sidebarBottom);
  else root.style.removeProperty('--sidebar-bottom');

  // Sidebar nav text color — also toggle a class for CSS overrides
  if (theme.sidebarText) {
    root.style.setProperty('--sidebar-text', theme.sidebarText);
    root.classList.add('custom-sidebar-text');
  } else {
    root.style.removeProperty('--sidebar-text');
    root.classList.remove('custom-sidebar-text');
  }

  // Primary button colors
  if (theme.buttonColor) {
    const hsl = hexToHSL(theme.buttonColor);
    if (hsl) root.style.setProperty('--primary', hsl);
  } else {
    root.style.removeProperty('--primary');
  }

  if (theme.buttonTextColor) {
    const hsl = hexToHSL(theme.buttonTextColor);
    if (hsl) root.style.setProperty('--primary-foreground', hsl);
  } else {
    root.style.removeProperty('--primary-foreground');
  }
}

export function clearThemeFromDOM() {
  const root = document.documentElement;
  ['--sidebar-top', '--sidebar-mid', '--sidebar-bottom', '--sidebar-text', '--primary', '--primary-foreground'].forEach(
    prop => root.style.removeProperty(prop)
  );
  root.classList.remove('custom-sidebar-text');
}

function hexToHSL(hex) {
  if (!hex || !hex.startsWith('#')) return null;
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  } else {
    return null;
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export const ThemeApplier = () => {
  const [loaded, setLoaded] = useState(false);

  const fetchAndApply = useCallback(async () => {
    try {
      const response = await warehouseAPI.getCompanyBranding();
      const theme = response.data?.theme;
      if (theme) {
        applyThemeToDOM(theme);
      }
    } catch {
      // Silent fail — theme is cosmetic
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchAndApply();
  }, [fetchAndApply]);

  // Listen for custom theme-updated events (fired from CompanySettings after save)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) applyThemeToDOM(e.detail);
      else clearThemeFromDOM();
    };
    window.addEventListener('theme-updated', handler);
    return () => window.removeEventListener('theme-updated', handler);
  }, []);

  return null; // This component has no visual output
};

export { DEFAULT_THEME };
